import { supabaseClient } from "@/lib/supabase";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@patient-watch/ui/sonner";

interface RecordExternalFormValidationData {
    form_id: string;
    is_valid: boolean;
    reason?: string;
    on_behalf_of_name: string;
    on_behalf_of_organisation?: string;
}

interface RecordExternalScheduleValidationData {
    schedule_id: string;
    is_valid: boolean;
    reason?: string;
    on_behalf_of_name: string;
    on_behalf_of_organisation?: string;
}

async function recordExternalFormValidation(data: RecordExternalFormValidationData) {
    const { error } = await supabaseClient.rpc("record_external_form_validation", {
        p_form_id: data.form_id,
        p_is_valid: data.is_valid,
        // The generated arg type says string, but the column/param is nullable text.
        p_reason: (data.reason ?? null) as string,
        p_on_behalf_of_name: data.on_behalf_of_name,
        p_on_behalf_of_organisation: data.on_behalf_of_organisation,
    });
    if (error) throw error;
    return data;
}

async function recordExternalScheduleValidation(data: RecordExternalScheduleValidationData) {
    const { error } = await supabaseClient.rpc("record_external_schedule_validation", {
        p_schedule_id: data.schedule_id,
        p_is_valid: data.is_valid,
        // The generated arg type says string, but the column/param is nullable text.
        p_reason: (data.reason ?? null) as string,
        p_on_behalf_of_name: data.on_behalf_of_name,
        p_on_behalf_of_organisation: data.on_behalf_of_organisation,
    });
    if (error) throw error;
    return data;
}

export function useRecordExternalFormValidation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["record-external-form-validation"],
        mutationFn: recordExternalFormValidation,
        onSuccess: (data) => {
            toast.success(
                data.is_valid
                    ? "Sign-off recorded. It is listed under Approved."
                    : "Rejection recorded. It is listed under Rejected."
            );
            queryClient.invalidateQueries({ queryKey: ["validations"] });
        },
        onError: (error: Error) => {
            toast.error(error.message || "Could not record this sign-off");
        },
    });
}

export function useRecordExternalScheduleValidation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ["record-external-schedule-validation"],
        mutationFn: recordExternalScheduleValidation,
        onSuccess: (data) => {
            toast.success(
                data.is_valid
                    ? "Sign-off recorded. It is listed under Approved."
                    : "Rejection recorded. It is listed under Rejected."
            );
            queryClient.invalidateQueries({ queryKey: ["validations"] });
        },
        onError: (error: Error) => {
            toast.error(error.message || "Could not record this sign-off");
        },
    });
}
